import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faXmarkCircle } from "@fortawesome/free-solid-svg-icons";
import Select from "react-select";
import toast from "react-hot-toast";
import AdminServiceV2 from "../../../services/admin.service.v2";
import AdminService from "../../../services/admin.service";
import AdminSupportUserTable from "../../../components/table/AdminSupportUser.table";
import ConfirmModal from "../../../components/Modal/Confirm.modal";

const emptyCreate = { email: "", name: "", password: "", exams: [] as any[] };
const emptyEdit = { password: "", allowed: true, exams: [] as any[] };

export default function AdminSupportUserIndex() {
  const [users, setUsers] = useState<any[]>([]);
  const [exams, setExams] = useState<{ value: string; label: string }[]>([]);

  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...emptyCreate });

  const [editUser, setEditUser] = useState<any>(null);
  const [editForm, setEditForm] = useState({ ...emptyEdit });

  const loadExams = async () => {
    await AdminService.getAllExamName()
      .then((res) => {
        if (res.status === 200) {
          setExams(res.data.data.examName.map((e: any) => ({ value: e._id, label: e.name })));
        }
      })
      .catch(() => {});
  };

  const loadUsers = async () => {
    await AdminServiceV2.getAdminSupportUsers()
      .then((res) => {
        if (res.status === 200) {
          setUsers(res.data.data?.adminSupportUsers || []);
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadExams();
    loadUsers();
  }, []);

  function handleCreateField(e: React.ChangeEvent<HTMLInputElement>) {
    setCreateForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleCreate() {
    if (!createForm.email || !createForm.name || !createForm.password) {
      toast.error("Email, name and password are required");
      return;
    }
    if (createForm.exams.length === 0) {
      toast.error("Please select at least one exam");
      return;
    }
    await AdminServiceV2.createAdminSupportUser({
      email: createForm.email,
      name: createForm.name,
      password: createForm.password,
      exams: createForm.exams.map((e: any) => e.value),
    })
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          toast.success("Support user created successfully");
          setShowCreate(false);
          setCreateForm({ ...emptyCreate });
          loadUsers();
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to create user");
      });
  }

  function openEdit(user: any) {
    setEditUser(user);
    setEditForm({
      password: "",
      allowed: user.allowed ?? true,
      exams: (user.examsAllowed || []).map((id: string) => {
        const match = exams.find((e) => e.value === id);
        return match || { value: id, label: id };
      }),
    });
  }

  async function handleUpdate() {
    if (editForm.exams.length === 0) {
      toast.error("Please select at least one exam");
      return;
    }
    const payload: any = {
      allowed: editForm.allowed,
      exams: editForm.exams.map((e: any) => e.value),
    };
    if (editForm.password) payload.password = editForm.password;

    await AdminServiceV2.updateAdminSupportUser(editUser._id, payload)
      .then((res) => {
        if (res.status === 200) {
          toast.success("Support user updated successfully");
          setEditUser(null);
          loadUsers();
        }
      })
      .catch((err) => {
        toast.error(err?.response?.data?.message || "Failed to update user");
      });
  }

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mt-2">
        <p style={{ fontSize: "18px", fontWeight: "bold" }} className="mb-0">
          Admin Support Users
        </p>
        <Button
          variant="outline-primary"
          className="fw-bold"
          size="sm"
          onClick={() => setShowCreate(true)}
        >
          Create User
        </Button>
      </div>

      <AdminSupportUserTable users={users} exams={exams} onEdit={openEdit} />

      {/* Create modal */}
      <ConfirmModal
        titleText="Create Admin Support User"
        size="md"
        show={showCreate}
        onHide={() => { setShowCreate(false); setCreateForm({ ...emptyCreate }); }}
        bodyText={
          <>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">Name</label>
              <Form.Control
                type="text"
                name="name"
                value={createForm.name}
                placeholder="e.g. support1"
                onChange={handleCreateField}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">Email</label>
              <Form.Control
                type="text"
                name="email"
                value={createForm.email}
                placeholder="e.g. support_user@enixm"
                onChange={handleCreateField}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">Password</label>
              <Form.Control
                type="password"
                name="password"
                value={createForm.password}
                placeholder="Enter password"
                onChange={handleCreateField}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">Exams</label>
              <Select
                isMulti
                options={exams}
                value={createForm.exams}
                onChange={(sel) => setCreateForm((prev) => ({ ...prev, exams: sel as any[] }))}
                placeholder="Select exams..."
              />
            </Form.Group>
          </>
        }
        button={[
          {
            label: <><FontAwesomeIcon icon={faXmarkCircle} /> Cancel</>,
            action: () => { setShowCreate(false); setCreateForm({ ...emptyCreate }); },
            variant: "secondary",
          },
          {
            label: <><FontAwesomeIcon icon={faCheck} /> Create</>,
            action: handleCreate,
            variant: "success",
          },
        ]}
      />

      {/* Edit modal */}
      <ConfirmModal
        titleText="Edit Support User"
        size="md"
        show={!!editUser}
        onHide={() => setEditUser(null)}
        bodyText={
          <>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">New Password <span className="text-muted">(leave blank to keep unchanged)</span></label>
              <Form.Control
                type="password"
                value={editForm.password}
                placeholder="Enter new password"
                onChange={(e) => setEditForm((prev) => ({ ...prev, password: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">Status</label>
              <Select
                options={[{ value: true, label: "Active" }, { value: false, label: "Blocked" }]}
                value={editForm.allowed
                  ? { value: true, label: "Active" }
                  : { value: false, label: "Blocked" }}
                onChange={(sel: any) => setEditForm((prev) => ({ ...prev, allowed: sel.value }))}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <label className="fw-bold xcn-text-12 text-secondary">Exams</label>
              <Select
                isMulti
                options={exams}
                value={editForm.exams}
                onChange={(sel) => setEditForm((prev) => ({ ...prev, exams: sel as any[] }))}
                placeholder="Select exams..."
              />
            </Form.Group>
          </>
        }
        button={[
          {
            label: <><FontAwesomeIcon icon={faXmarkCircle} /> Cancel</>,
            action: () => setEditUser(null),
            variant: "secondary",
          },
          {
            label: <><FontAwesomeIcon icon={faCheck} /> Save</>,
            action: handleUpdate,
            variant: "success",
          },
        ]}
      />
    </>
  );
}
